import { useMemo } from 'react'

import { useRoles } from './useRoles'

/**
 * Prefixes of the fine-grained RBAC entries Keycloak emits inside the same
 * `resource_access['aos-oidc'].roles` claim as the legacy role names:
 * `aos:{domain}:{level}` permissions and `profile:*` composites. Plain
 * membership checks are sufficient — Keycloak grants every level explicitly
 * (a profile holding `aos:event:admin` also carries `:write` and `:read`),
 * so no level-hierarchy logic belongs on the client.
 */
export const PERMISSION_PREFIX = 'aos:'
export const PROFILE_PREFIX = 'profile:'

const filterByPrefix = (roles: readonly string[], prefix: string): readonly string[] =>
    roles.filter(entry => entry.startsWith(prefix))

/** `aos:*` permissions from the current session, or `[]` when no session. */
export const usePermissions = (): readonly string[] => {
    const roles = useRoles()
    return useMemo(() => filterByPrefix(roles, PERMISSION_PREFIX), [roles])
}

/** `profile:*` composites from the current session, or `[]` when no session. */
export const useProfiles = (): readonly string[] => {
    const roles = useRoles()
    return useMemo(() => filterByPrefix(roles, PROFILE_PREFIX), [roles])
}

/**
 * Convenience predicate: does the current session hold `permission`?
 * Accepts only `aos:*` strings — a legacy role name returns `false` even
 * when the session holds it, so call sites cannot silently keep gating by
 * legacy vocabulary through this hook (use `useHasRole` for that).
 */
export const useHasPermission = (permission: string): boolean => {
    const roles = useRoles()
    return useMemo(
        () => permission.startsWith(PERMISSION_PREFIX) && roles.includes(permission),
        [roles, permission],
    )
}

/** Convenience predicate: does the current session hold any of `permissions`? */
export const useHasAnyPermission = (permissions: readonly string[]): boolean => {
    const roles = useRoles()
    return useMemo(
        () => permissions.some(p => p.startsWith(PERMISSION_PREFIX) && roles.includes(p)),
        [roles, permissions],
    )
}

/**
 * Convenience predicate: does the current session hold `profile`?
 * Accepts only `profile:*` strings — see {@link useHasPermission}.
 */
export const useHasProfile = (profile: string): boolean => {
    const roles = useRoles()
    return useMemo(
        () => profile.startsWith(PROFILE_PREFIX) && roles.includes(profile),
        [roles, profile],
    )
}

/** Convenience predicate: does the current session hold any of `profiles`? */
export const useHasAnyProfile = (profiles: readonly string[]): boolean => {
    const roles = useRoles()
    return useMemo(
        () => profiles.some(p => p.startsWith(PROFILE_PREFIX) && roles.includes(p)),
        [roles, profiles],
    )
}
